import React, { useEffect } from 'react'
import { useForm, Controller, FieldErrors } from 'react-hook-form'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { convertDateForInput, formatDateByLocale } from '@/lib/utils'
import { Badge } from '@/components/ui/badge/badge'
import { Field, FormData, NonRowField } from './types'

interface DynamicFormProps {
  fields: Field[]
  onSubmit: (data: FormData) => void
  defaultValues?: FormData
  formRef?: React.RefObject<{ submit: () => void }>
  sections?: Array<{
    id: string
    label: string
    fields: Field[]
    collapsed?: boolean
    onToggle?: (id: string) => void
  }>
  isViewOnly?: boolean
}

const DynamicForm: React.FC<DynamicFormProps> = ({
  fields,
  onSubmit,
  defaultValues = {},
  formRef,
  sections,
  isViewOnly,
}) => {
  // Helper function to process default values for date fields and dropdown preselection
  const processDefaultValues = (values: FormData): FormData => {
    const processed = { ...values }

    // Get all field information
    const allFields = sections ? sections.flatMap(s => s.fields) : fields
    const dateFieldInfo = new Map<string, 'date' | 'datetime-local'>()
    const selectFieldInfo = new Map<
      string,
      { value: string; label: string }[]
    >()

    const collectFieldInfo = (fieldList: Field[]) => {
      fieldList.forEach(field => {
        if (field.type === 'row') {
          field.fields.forEach(f => {
            if (f.type === 'date' || f.type === 'datetime-local') {
              dateFieldInfo.set(f.name, f.type)
            } else if (f.type === 'select' && f.options) {
              selectFieldInfo.set(f.name, f.options)
            }
          })
        } else if (field.type === 'date' || field.type === 'datetime-local') {
          dateFieldInfo.set(field.name, field.type)
        } else if (field.type === 'select' && field.options) {
          selectFieldInfo.set(field.name, field.options)
        }
      })
    }

    collectFieldInfo(allFields)

    // console.log('Date fields found:', Array.from(dateFieldInfo.entries()))
    // console.log('Select fields found:', Array.from(selectFieldInfo.entries()))
    // console.log('Original default values:', values)

    // Helper function to find matching option value
    const findMatchingOptionValue = (
      backendValue: string,
      options: { value: string; label: string }[],
    ): string => {
      if (!backendValue) return ''

      // Exact value match
      const exactMatch = options.find(opt => opt.value === backendValue)
      if (exactMatch) return exactMatch.value

      //  Exact label match (case-insensitive)
      const labelMatch = options.find(
        opt => opt.label.toLowerCase() === backendValue.toLowerCase(),
      )
      if (labelMatch) return labelMatch.value

      //  Case-insensitive value match
      const valueMatchCI = options.find(
        opt => opt.value.toLowerCase() === backendValue.toLowerCase(),
      )
      if (valueMatchCI) return valueMatchCI.value

      // Partial label match (case-insensitive)
      const partialMatch = options.find(
        opt =>
          opt.label.toLowerCase().includes(backendValue.toLowerCase()) ||
          backendValue.toLowerCase().includes(opt.label.toLowerCase()),
      )
      if (partialMatch) return partialMatch.value

      // Value contains backend value or vice versa (case-insensitive)
      const valueMatch = options.find(
        opt =>
          opt.value.toLowerCase().includes(backendValue.toLowerCase()) ||
          backendValue.toLowerCase().includes(opt.value.toLowerCase()),
      )
      if (valueMatch) return valueMatch.value

      // Handle common status mappings
      const statusMappings: Record<string, string> = {
        new: 'open',
        active: 'in-progress',
        resolved: 'closed',
        completed: 'closed',
        pending: 'open',
        draft: 'open',
      }

      const mappedValue = statusMappings[backendValue.toLowerCase()]
      if (mappedValue) {
        const mappedOption = options.find(opt => opt.value === mappedValue)
        if (mappedOption) {
          // console.log(
          //   `Status mapping applied: "${backendValue}" -> "${mappedValue}"`,
          // )
          return mappedOption.value
        }
      }

      console.warn(
        `No matching option found for value "${backendValue}" in options:`,
        options,
      )
      return backendValue // Return original value if no match found
    }

    Object.keys(processed).forEach(key => {
      const value = processed[key]
      if (typeof value === 'string' && value) {
        // Handle date fields
        const fieldType = dateFieldInfo.get(key)
        const looksLikeDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)

        if (fieldType) {
          const converted = convertDateForInput(value, fieldType)
          // console.log(`Converting date field ${key}: ${value} -> ${converted}`)
          processed[key] = converted
        } else if (looksLikeDate) {
          const converted = convertDateForInput(value, 'datetime-local')
          // console.log(
          //   `Converting date-like field ${key}: ${value} -> ${converted}`,
          // )
          processed[key] = converted
        }

        const selectOptions = selectFieldInfo.get(key)
        if (selectOptions) {
          const matchedValue = findMatchingOptionValue(value, selectOptions)
          if (matchedValue !== value) {
            console.log(
              `Matching select field ${key}: "${value}" -> "${matchedValue}"`,
            )
          }
          processed[key] = matchedValue
        }
      }
    })

    // console.log('Processed default values:', processed)
    return processed
  }

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: processDefaultValues(defaultValues),
    mode: 'onChange',
  })

  // const isDisabled = isViewOnly || field.disabled

  React.useImperativeHandle(formRef, () => {
    // console.log('Creating form ref with submit function')
    return {
      submit: () => {
        // console.log('Form submit called via ref')
        // console.log('Form errors:', errors)
        const submitHandler = handleSubmit(
          data => {
            // console.log('Form validation passed, calling onSubmit')
            onSubmit(data)
          },
          errors => {
            console.log('Form validation failed:', errors)
          },
        )
        submitHandler()
      },
    }
  })

  useEffect(() => {
    const subscription = watch((values, info) => {
      const dependencyName = info.name

      if (!dependencyName) return

      const allFieldsToCheck = sections
        ? sections.flatMap(s => s.fields)
        : fields

      allFieldsToCheck.forEach(field => {
        if (
          field.type !== 'row' &&
          field.dependsOn === info.name &&
          field.onDependencyChange
        ) {
          const dependencyValue = values[dependencyName]
          field.onDependencyChange(String(dependencyValue || ''), setValue)
        }
      })
    })

    return () => subscription.unsubscribe()
  }, [watch, fields, sections, setValue])

  const renderField = (field: NonRowField) => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'number':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.rules}
            render={({ field: rhf }) => (
              <input
                {...rhf}
                id={field?.name}
                value={String(rhf.value || '')}
                type={field.type}
                placeholder={field.placeholder}
                onChange={e => {
                  if (field.allowPattern) {
                    const originalValue = e.target.value
                    const filteredValue = originalValue
                      .split('')
                      .filter(char => field.allowPattern?.test(char))
                      .join('')
                    rhf.onChange(filteredValue)
                  } else {
                    rhf.onChange(e)
                  }
                }}
                disabled={isViewOnly || field.disabled}
                className={`w-full rounded-radius-sm border border-border px-3 py-2 ${
                  isViewOnly || field.disabled
                    ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                    : 'bg-primary'
                }`}
              />
            )}
          />
        )

      case 'textarea':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.rules}
            render={({ field: rhf }) => (
              <textarea
                {...rhf}
                id={field?.name}
                value={String(rhf.value || '')}
                onChange={e => {
                  if (field.allowPattern) {
                    const filteredValue = e.target.value
                      .split('')
                      .filter(char => field.allowPattern?.test(char))
                      .join('')
                    rhf.onChange(filteredValue)
                  } else {
                    rhf.onChange(e)
                  }
                }}
                rows={field.rows ?? 3}
                placeholder={field.placeholder}
                disabled={isViewOnly || field.disabled}
                className='w-full rounded-radius-sm border border-border px-3 py-3'
              />
            )}
          />
        )

      case 'select':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.rules}
            render={({ field: rhf }) => {
              if (isViewOnly || field.disabled) {
                const selectedOption = field.options?.find(
                  opt => opt.value === rhf.value,
                )
                const displayValue = selectedOption
                  ? selectedOption.label
                  : String(rhf.value || '')

                return (
                  <div className='w-full rounded-radius-sm border border-border bg-primary px-3 py-2 text-primary-foreground font-semibold shadow-sm'>
                    {displayValue || 'No selection'}
                  </div>
                )
              }

              return (
                <select
                  {...rhf}
                  id={field?.name}
                  value={String(rhf.value || '')}
                  onChange={e => {
                    rhf.onChange(e)
                    field.onChange?.(e.target.value, setValue)
                  }}
                  disabled={isViewOnly || field.disabled}
                  className='w-full rounded-radius-sm border border-border bg-primary px-3 py-2'
                >
                  <option value=''>{field.placeholder ?? 'Select...'}</option>
                  {field.options?.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )
            }}
          />
        )

      case 'date':
      case 'datetime-local':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.rules}
            render={({ field: rhf }) => {
              if (isViewOnly || field.disabled) {
                const displayValue = rhf.value
                  ? (() => {
                      try {
                        let dateToFormat = String(rhf.value)
                        if (dateToFormat) {
                          // If it's in HTML input format, convert to full ISO
                          if (
                            field.type === 'date' &&
                            /^\d{4}-\d{2}-\d{2}$/.test(dateToFormat)
                          ) {
                            dateToFormat = `${dateToFormat}T00:00:00.000Z`
                          } else if (
                            field.type === 'datetime-local' &&
                            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateToFormat)
                          ) {
                            dateToFormat = `${dateToFormat}:00.000Z`
                          }
                          return formatDateByLocale(dateToFormat)
                        }
                        return ''
                      } catch (error) {
                        console.error(error)
                        return String(rhf.value || '')
                      }
                    })()
                  : ''

                return (
                  <div className='w-full rounded-radius-sm border border-border bg-primary px-3 py-2 text-primary-foreground'>
                    {displayValue || 'No date set'}
                  </div>
                )
              }

              return (
                <input
                  {...rhf}
                  id={field?.name}
                  value={String(rhf.value || '')}
                  type={field.type}
                  disabled={isViewOnly || field.disabled}
                  className='w-full rounded-radius-sm border border-border px-3 py-2'
                />
              )
            }}
          />
        )

      case 'multiselect-email':
        // Renders email addresses as pills when in view-only mode
        // Expects a string with emails separated by the specified separator (default: semicolon)
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.rules}
            render={({ field: rhf }) => {
              const separator = field.separator || ';'
              const emailString = String(rhf.value || '')
              const emails = emailString
                ? emailString
                    .split(separator)
                    .map(email => email.trim())
                    .filter(email => email)
                : []

              if (isViewOnly || field.disabled) {
                return (
                  <div className='w-full min-h-[2.75rem] max-h-[3.75rem] overflow-y-auto rounded-radius-sm border border-border bg-primary px-3 py-2 spec-scroll'>
                    {emails.length > 0 ? (
                      <div className='flex flex-wrap gap-2'>
                        {emails.map((email, index) => (
                          <Badge
                            key={index}
                            variant='secondary'
                            className='text-kpi-supporting-1'
                          >
                            {email}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className='text-primary-foreground'>No emails</span>
                    )}
                  </div>
                )
              }

              // For editable mode (though you mentioned it's view-only, keeping this for completeness)
              return (
                <input
                  {...rhf}
                  id={field?.name}
                  value={emailString}
                  type='text'
                  placeholder={field.placeholder}
                  disabled={isViewOnly || field.disabled}
                  className='w-full rounded-radius-sm border border-border px-3 py-2'
                />
              )
            }}
          />
        )

      default:
        return null
    }
  }

  const renderFields = (list: Field[]) =>
    list.map((field, index) => {
      if (field.type === 'row') {
        return (
          <div key={index} className='grid grid-cols-2 gap-4'>
            {field.fields.map(f => (
              <div key={f.name}>
                <label
                  htmlFor={f.name}
                  className='block text-kpi-heading text-primary-foreground font-normal'
                >
                  {f.label}
                </label>
                {renderField(f)}
                {errors[f.name] && (
                  <p className='text-danger text-kpi-supporting-1'>
                    {
                      (errors as FieldErrors<FormData>)[f.name]
                        ?.message as string
                    }
                  </p>
                )}
              </div>
            ))}
          </div>
        )
      }

      return (
        <div key={field.name} className={field.className}>
          <label
            htmlFor={field.name}
            className='block text-kpi-heading text-primary-foreground font-normal'
          >
            {field.label}
          </label>
          {renderField(field)}
          {errors[field.name] && (
            <p className='text-danger text-kpi-supporting-1'>
              {(errors as FieldErrors<FormData>)[field.name]?.message as string}
            </p>
          )}
        </div>
      )
    })

  return (
    <div className='space-y-4'>
      {sections
        ? sections.map(section => (
            <div
              key={section.id}
              className='flex flex-col gap-[0.75rem]  last:border-0'
            >
              {section.onToggle ? (
                <button
                  type='button'
                  onClick={() => section.onToggle?.(section.id)}
                  className='w-full flex items-center justify-between p-[0.75rem] hover:bg-info-dim-light rounded-[0.75rem] border-2 border-solid shadow-[8px_12px_24px_0_rgba(0,0,0,0.10)]'
                >
                  <h3 className='text-kpi-supporting-1 font-semibold text-primary-foreground'>
                    {section.label}
                  </h3>
                  {section.collapsed ? (
                    <ChevronDown
                      size={18}
                      className='text-primary-foreground'
                    />
                  ) : (
                    <ChevronUp size={18} className='text-primary-foreground' />
                  )}
                </button>
              ) : (
                <h3 className='text-kpi-heading-2 font-semibold text-secondary-foreground py-2'>
                  {section.label}
                </h3>
              )}
              {(!section.collapsed || !section.onToggle) && (
                <div className='flex flex-col gap-[0.75rem] py-[0.75rem] text-primary-foreground'>
                  {renderFields(section.fields)}
                </div>
              )}
            </div>
          ))
        : renderFields(fields)}
    </div>
  )
}

export default DynamicForm
